<template>
  <div
    class="input-group mbr-input"
    :class="`input-group-${variant} ${getStatus(error, success)}`"
  >
    <label class="mbr-input__label" :class="variant === 'static' ? '' : 'form-label'">{{ label }}</label>
    <input
      autocomplete="nope"
      :id="id"
      :type="type"
      class="form-control"
      :class="[getClasses(size), extraClass, { 'mbr-input__field': !isLegacyField }]"
      :name="name"
      :value="modelValue"
      @input="$emit('update:modelValue', $event.target.value)"
      :placeholder="placeholder"
      :isRequired="isRequired"
      :disabled="disabled"
      :maxlength="maxLength"
      :readonly="readonly"
    />
  </div>
</template>

<script>
import setMaterialInput from "@/assets/js/material-input.js";

export default {
  name: "MaterialInput",
  props: {
    variant: {
      type: String,
      default: "dynamic",
    },
    label: {
      type: String,
      default: "",
    },
    size: {
      type: String,
      default: "default",
    },
    success: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },

    name: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      required: true,
    },
    modelValue: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    extraClass: {
      type: String,
      default: "",
    },
    maxLength: {
      type: Number,
      default: 1000,
    },
  },
  computed: {
    // Views that draw their own field (borderless value rows, the SignIn
    // underline phone field) keep the legacy look until their screen is reskinned.
    isLegacyField() {
      return /\b(input-none-border|border-bottom-green)\b/.test(this.extraClass);
    },
  },
  mounted() {
    setMaterialInput();
  },
  methods: {
    getClasses: (size) => {
      let sizeValue;

      sizeValue = size ? `form-control-${size}` : null;

      return sizeValue;
    },
    getStatus: (error, success) => {
      let isValidValue;

      if (success) {
        isValidValue = "is-valid";
      } else if (error) {
        isValidValue = "is-invalid";
      } else {
        isValidValue = null;
      }

      return isValidValue;
    },
  },
};
</script>

<style scoped>
/* Selectors are doubled up and use !important to outrank the Material Dashboard
   underline rules and custom.css, which both style .input-group .form-control. */
.input-group.mbr-input .mbr-input__label {
  position: static !important;
  display: block;
  flex: 0 0 100%;
  margin: 0 0 var(--mbr-space-sm) 0;
  font-family: var(--mbr-font-body);
  font-size: var(--mbr-label-caps-size) !important;
  font-weight: var(--mbr-label-caps-weight);
  line-height: var(--mbr-label-caps-line);
  letter-spacing: var(--mbr-label-caps-tracking);
  text-transform: uppercase;
  color: var(--mbr-color-ink-muted) !important;
}

.input-group.mbr-input .mbr-input__label:empty {
  display: none;
}

.input-group.mbr-input .form-control.mbr-input__field {
  width: 100%;
  height: 56px;
  margin-left: 0 !important;
  /* Left inset via text-indent: SignIn.vue's unscoped `#phone_number { padding: 0 !important }`
     leaks to every #phone_number input and an id selector outranks this rule. */
  padding: 0 var(--mbr-space-md) 0 0 !important;
  text-indent: var(--mbr-space-md);
  background: var(--mbr-color-surface) !important;
  border: 1px solid var(--mbr-color-outline) !important;
  border-radius: var(--mbr-radius-lg) !important;
  font-family: var(--mbr-font-body);
  font-size: var(--mbr-body-md-size);
  font-weight: 600;
  line-height: var(--mbr-body-md-line);
  color: var(--mbr-color-ink-headline);
  box-shadow: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

/* SignIn / ChangePasscode / ForgotPassword center `input::placeholder` globally */
.input-group.mbr-input .form-control.mbr-input__field::placeholder {
  color: var(--mbr-color-ink-muted);
  font-size: var(--mbr-body-md-size);
  font-weight: 500;
  text-align: start;
}

.input-group.mbr-input .form-control.mbr-input__field:focus {
  border-color: var(--mbr-color-primary) !important;
  box-shadow: 0 0 0 0.5px var(--mbr-color-primary);
}

.input-group.mbr-input .form-control.mbr-input__field[readonly],
.input-group.mbr-input .form-control.mbr-input__field:disabled {
  background: var(--mbr-color-tint) !important;
  box-shadow: none;
}

.input-group.mbr-input .form-control.mbr-input__field:disabled {
  cursor: not-allowed;
}

.input-group.mbr-input.is-invalid .form-control.mbr-input__field {
  border-color: var(--mbr-color-error) !important;
}

.input-group.mbr-input.is-invalid .form-control.mbr-input__field:focus {
  box-shadow: 0 0 0 0.5px var(--mbr-color-error);
}
</style>
